import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import {
  getSeverityBackgroundColor,
  getSeverityTextColor,
} from '../../../theme/colorMapper';

/**
 * TemplateEffectivenessInsightCard Component
 * Special accordion card for template effectiveness insights with recommendations
 */
const TemplateEffectivenessInsightCard = ({ insight }) => {
  const theme = useTheme();

  const getSeverityIcon = severity => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon />;
      case 'high':
        return <WarningIcon />;
      case 'medium':
      case 'low':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  return (
    <Accordion
      sx={{
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: 'none',
        height: '100%',
        minHeight: '240px',
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: 0,
        },
        '&:hover': {
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: theme.palette.text.secondary }} />
        }
        sx={{
          borderRadius: '8px',
          minHeight: '56px',
          alignItems: 'center',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            margin: '12px 0',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            minWidth: 0,
          }}
        >
          {getSeverityIcon(insight.severity)}
          <Typography
            sx={{
              flex: 1,
              fontWeight: 500,
              color: theme.palette.text.primary,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {insight.template_name || insight.template_id}
          </Typography>
          <Chip
            label={insight.severity}
            size="small"
            sx={{
              backgroundColor: getSeverityBackgroundColor(
                theme,
                insight.severity
              ),
              color: getSeverityTextColor(theme, insight.severity),
              border: `1px solid ${theme.palette.divider}`,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: '24px',
              flexShrink: 0,
            }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          pt: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            mb: 2,
            lineHeight: 1.6,
            flex: 1,
            width: '100%',
            fontSize: '0.875rem',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            overflow: 'hidden',
          }}
        >
          {insight.summary}
        </Typography>
        {insight.recommendations && insight.recommendations.length > 0 && (
          <Box sx={{ mt: 'auto', width: '100%' }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Recommendations:
            </Typography>
            <List dense>
              {insight.recommendations.map((rec, idx) => (
                <ListItem key={idx} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {rec.title}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                      >
                        {rec.description}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

TemplateEffectivenessInsightCard.propTypes = {
  insight: PropTypes.object.isRequired,
};

export default TemplateEffectivenessInsightCard;
